import { useEffect } from 'react';

/**
 * Selectors for elements where the native browser context menu is
 * intentionally kept (input editing, code editor, terminal, preformatted
 * text, explicit opt-ins).
 *
 * NOT included — even though users can select text inside them:
 *   `.ag-cell` — TableView installs its own dbgo cell context menu with
 *   Edit / Set NULL / Copy / Paste / Cut / Insert Row / Delete, so the
 *   native menu would duplicate that AND let iPadOS Writing Tools surface.
 *   Users can still double-click to select cell text and Cmd+C.
 */
const NATIVE_MENU_ALLOWED = [
  'input',
  'textarea',
  'select',
  '[contenteditable="true"]',
  '[contenteditable=""]',
  '.monaco-editor',
  '.xterm',
  'pre',
  'code',
  '.dbgo-selectable',
].join(',');

/**
 * Globally suppress the browser's native context menu.
 *
 * On iPadOS with a trackpad or Magic Keyboard attached, right-click fires a
 * `contextmenu` event AND Safari layers Writing Tools onto the native
 * context menu popup. Even with `user-select: none` at the root, Safari
 * still surfaces Writing Tools for any right-click where the browser would
 * otherwise show its default menu. We preventDefault at the document level
 * so the native popup (and Writing Tools along with it) never appears.
 *
 * Our own dbgo context menus are unaffected — they call `preventDefault`
 * themselves and then render their menu. This listener just kills the
 * native fallback for every surface that doesn't have its own context
 * handler (stat cards, headers, Actions panel rows, info tables, …).
 *
 * Allowlisted surfaces (inputs, Monaco, xterm, AG cells, pre/code) still
 * get the native menu so users can Copy / Look Up / Paste as usual.
 */
export function useSuppressNativeContextMenu(): void {
  useEffect(() => {
    const onContextMenu = (e: MouseEvent) => {
      if (e.defaultPrevented) return; // app-level handler already took it
      const target = e.target as Element | null;
      if (target && target.closest(NATIVE_MENU_ALLOWED)) return;
      e.preventDefault();
    };
    document.addEventListener('contextmenu', onContextMenu);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu);
    };
  }, []);
}
